import { useState } from "react";
import { format, isToday } from "date-fns";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { colors, isBeforeToday } from "~/utils";
import type { Styled } from "~/utils";
import { ErrorLabel } from "../ErrorLabel";
import { DateIcon } from "../Icons";
import { Label } from "../Label";

interface DatePickerProps extends Styled {
  label?: string;
  id: string;
  value?: Date;
  onChange: (d: Date) => void;
  error?: string;
}

export const DatePicker = ({
  label,
  id,
  value,
  onChange,
  error,
  className,
}: DatePickerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className={twMerge("flex w-full flex-col overflow-hidden", className)}>
      {label && <Label className="mb-2">{label}</Label>}
      <button
        type="button"
        onClick={() => setIsModalOpen((prev) => !prev)}
        className={twMerge(
          "relative flex w-full items-center justify-between rounded-lg border border-neutral-variant-60 bg-surface-container-low px-4 py-3.5 dark:bg-surface-container-low-dark",
          error && "border-error-70",
        )}
      >
        <span className="h-6 w-full text-left text-base text-secondary-20 placeholder-neutral-variant-60 dark:text-secondary">
          {value ? format(value, t("common.date_format_month_first")) : ""}
        </span>
        <DateIcon className="size-5" />
      </button>
      {error && <ErrorLabel>{error}</ErrorLabel>}

      <div
        className={twMerge(
          "invisible absolute right-8 z-30 mt-24 rounded-2xl bg-surface-container-highest p-4 text-secondary-40 dark:bg-surface-container-high-dark dark:text-secondary-95",
          isModalOpen && "visible",
        )}
      >
        <DayPicker
          mode="single"
          id={id}
          modifiersStyles={{
            today: { fontWeight: 400 },
            selected: {
              color: colors.primary[20],
              backgroundColor: colors.primary.DEFAULT,
            },
          }}
          defaultMonth={value}
          classNames={{
            button:
              "hover:bg-neutral-variant-30 hover:cursor-pointer disabled:text-neutral-60 disabled:hover:bg-transparent disabled:hover:cursor-default",
          }}
          selected={value}
          onDayClick={(e) => {
            onChange(e);
            setIsModalOpen(false);
          }}
          modifiersClassNames={{
            selected: "bg-green-200",
          }}
          disabled={(d) => isBeforeToday(d) || isToday(d)}
        />
      </div>
    </div>
  );
};
